/* @flow */
import {nth} from 'lodash';

const REGEX_FACEBOOK_V1 = /((?:https?:)?\/\/(?:www\.)?facebook\.com\/[\w\d._-]+\/(?:posts|videos|photos|activity)\/[\d\w:.]+\/(?:[\d\w:.]+\/)?)/;
const REGEX_FACEBOOK_V2 = /"((?:https?:)?\/\/(?:www\.)?facebook\.com\/(?:permalink|photo)\.php.+?)"/;
export const REGEX_FACEBOOK_V3 = /((?:https?:)?\/\/(?:www\.)?facebook\.com\/plugins\/(?:video|post)\.php.+?)?href=([a-zA-Z0-9!@#$%_\-.,;:]+)/;
export const REGEX_INSTAGRAM = /((https?:)?\/\/(www\.){0,1}instagram\.com\/p\/([a-zA-Z0-9+&@#%?=~_!:,.;-]+)\/?)/;
export const REGEX_INSTAGRAM_JS = /platform\.instagram\.com\/en_US\/embeds\.js/;
const REGEX_PINTEREST = /https?:\/\/(www\.){0,1}pinterest\.(com|co|de|fr|ca){0,1}/;
export const REGEX_TWITTER = /(https?:\/\/(www\.){0,1}twitter\.com\/[a-zA-Z0-9+&@#%?=~_!:,.;]+\/status(es){0,1}\/(\d+))/;
export const REGEX_TWITTER_TAG = /twitter-tweet/;
const REGEX_VIDEO_AOL_LEGACY = /\/\/embed.5min.com\/PlayerSeed\//;
export const REGEX_VIDEO_AOL = /delivery\.vidible\.tv\/jsonp\/pid=([a-zA-Z0-9!@#$%_\-.,;:]+)\/vid=([a-zA-Z0-9!@#$%_\-.,;:]+)\/([a-zA-Z0-9!@#$%_\-.,;:]+).js/;
const REGEX_VIDEO_DAILYMOTION = /(?:(?:https?:)?\/\/(?:www\.)?)?dailymotion\.com\/embed\/video\/[a-zA-Z0-9!@#$%_\-.,;:]+/;
const REGEX_VIDEO_EKO = /eko\.com/;
const REGEX_VIDEO_FACEBOOK = /"((?:https?:)?\/\/(?:www\.)?facebook\.com\/plugins\/(?:video)\.php.+?)"/;
const REGEX_VIDEO_FUNNYORDIE = /(?:(?:https?:)?\/\/(?:www\.)?)?funnyordie\.com\/embed\/[a-zA-Z0-9!@#$%_\-.,;:]+/;
const REGEX_VIDEO_JW = /(?:(?:https?:)?\/\/(?:www\.)?)?content\.jwplatform\.com\/players\/[a-zA-Z0-9!@#$%_\-.,;:]+/;
const REGEX_VIDEO_MTV = /(?:https?:)?\/\/media\.mtvnservices\.com\/embed\/[a-zA-Z0-9!@#$%_\-.,;:]+/;
const REGEX_VIDEO_VIMEO = /(?:https?:)?\/\/(?:player\.)vimeo\.com\/video\/[a-zA-Z0-9!@#$%_\-.,;:]+/;
const REGEX_VIDEO_VINE = /(?:https?:)?\/\/(?:www\.)?vine\.co\/v\/[a-zA-Z0-9!@#$%_\-.,;:]+\/embed\/(?:simple|postcard)/;
export const REGEX_VIDEO_YOUTUBE = /(?:(?:https?:)?\/\/(?:www\.)?)?youtube\.com\/embed\/([a-zA-Z0-9!@#$%_\-.,;:]+)/;
export const REGEX_VIDEO_YOUTUBE_PLAYLIST = /(?:(?:https?:)?\/\/(?:www\.)?)?youtube\.com\/embed\/videoseries\?list=([a-zA-Z0-9!@#$%_\-.,;:]+)/;
const REGEX_PLAYBUZZ_QUIZ = /cdn\.playbuzz\.com\/widget\/feed\.js/;
const REGEX_EMAIL_SIGNUP = /jump\.refinery29\.com/;
const REGEX_SOUNDCLOUD = /https?:\/\/(www\.){0,1}(www\.){0,1}(w\.){0,1}soundcloud\.com/;
const REGEX_SPOTIFY = /https?:\/\/(www\.){0,1}(embed|open).spotify\.com/;
const REGEX_GET_EMBED = /r29-custom-embed/;
export const REGEX_TIKTOK = /https?:\/\/(?:www\.)?tiktok.com\/(?:[^/]+\/)?(?:video|embed)\/(\d+)\/?/;
/* generic embed detection looks for common embed tags, or other tags with attributes that explicitly contain embed keywords, imports without protocols, and common attribute names or values */
export const REGEX_EMBED = /(<(iframe|script|embed)\s+|(<[^>]+(plugin|widget|embed|["']\/\/|['"]twitter-t[^'"]+['"]|data-href[^\s]+['"]|data-pin[^\s]+['"]|data-inst[^\s]+['"])[^>]*>))/;

export const isAolVideo = (resource: string): bool =>
  REGEX_VIDEO_AOL.test(resource);

export const isAolVideoLegacy = (resource: string): bool =>
  REGEX_VIDEO_AOL_LEGACY.test(resource);

export const isDailymotion = (resource: string): bool =>
  REGEX_VIDEO_DAILYMOTION.test(resource);

export const isEkoVideo = (resource: string): bool =>
  REGEX_VIDEO_EKO.test(resource);

export const isEmailSignup = (resource: string): bool =>
  REGEX_EMAIL_SIGNUP.test(resource);

export const isFacebookVideo = (resource: string): bool =>
  REGEX_VIDEO_FACEBOOK.test(resource);

export const isFunnyOrDie = (resource: string): bool =>
  REGEX_VIDEO_FUNNYORDIE.test(resource);

export const isInstagram = (resource: string): bool =>
  REGEX_INSTAGRAM.test(resource) || resource.includes('platform.instagram.com/en_US/embeds.js');

export const isJWVideo = (resource: string): bool =>
  REGEX_VIDEO_JW.test(resource);

export const isMTV = (resource: string): bool =>
  REGEX_VIDEO_MTV.test(resource);

export const isPinterest = (resource: string): bool =>
  REGEX_PINTEREST.test(resource);

export const isPlaybuzz = (resource: string): bool =>
  REGEX_PLAYBUZZ_QUIZ.test(resource);

export const isSoundcloud = (resource: string): bool =>
  REGEX_SOUNDCLOUD.test(resource);

export const isSpotify = (resource: string): bool =>
  REGEX_SPOTIFY.test(resource);

export const isTwitter = (resource: string): bool =>
  REGEX_TWITTER.test(resource) || resource.includes('twitter-tweet');

export const isVimeo = (resource: string): bool =>
  REGEX_VIDEO_VIMEO.test(resource);

export const isVine = (resource: string): bool =>
  REGEX_VIDEO_VINE.test(resource);

export const isYoutube = (resource: string): bool =>
  REGEX_VIDEO_YOUTUBE.test(resource);

export const isYoutubePlaylist = (resource: string): bool =>
  REGEX_VIDEO_YOUTUBE_PLAYLIST.test(resource);

export const isTikTok = (resource: string): bool =>
  REGEX_TIKTOK.test(resource);

/* General Widget Identifiers */

export const isFacebook = (resource: string): bool => {
  return REGEX_FACEBOOK_V1.test(resource)
    || REGEX_FACEBOOK_V2.test(resource)
    || REGEX_FACEBOOK_V3.test(resource)
    || REGEX_VIDEO_FACEBOOK.test(resource);
};

export const isFacebookPost = (resource: string): bool => {
  return REGEX_FACEBOOK_V1.test(resource)
    || REGEX_FACEBOOK_V2.test(resource)
    || REGEX_FACEBOOK_V3.test(resource);
};

export const isVideo = (resource: string): bool => {
  return isAolVideo(resource)
    || isDailymotion(resource)
    || isEkoVideo(resource)
    || isFacebookVideo(resource)
    || isFunnyOrDie(resource)
    || isJWVideo(resource)
    || isMTV(resource)
    || isVimeo(resource)
    || isVine(resource)
    || isYoutube(resource);
};

export const isGetEmbed = (resource: string): bool =>
  REGEX_GET_EMBED.test(resource);


/* Video Widget Helpers */

const REGEX_IFRAME_HEIGHT = /height=("|')(\S+)("|')/;
const REGEX_IFRAME_WIDTH = /width=("|')(\S+)("|')/;
export const findAspectRatioFromResource = (resource: string): string => {
  const heightAttr = nth(resource.match(REGEX_IFRAME_HEIGHT), 2);
  const widthAttr = nth(resource.match(REGEX_IFRAME_WIDTH), 2);

  /*
   * If the height or width is a percentage we cannot ascertain
   * the ratio, so we default to the most-common option of 16x9
   */
  if (/%/.test(heightAttr) || /%/.test(widthAttr)) {
    return '16x9';
  }

  const height = parseInt(heightAttr);
  const width = parseInt(widthAttr);

  const ratio = width / height;
  switch (ratio) {
    case 9 / 16 : return '9x16';
    case 1 : return '1x1';
    case 16 / 9 : return '16x9';
  }
  return '16x9';
};

/* Widget vs plain HTML */
export const mayBeWidget = (resource: string): bool =>
  REGEX_EMBED.test(resource);
